import MultiSlider from '@ptomasroos/react-native-multi-slider';
import type { FilterType } from '../../../../../../api/screensService/getScreensFilterOptions/enums';
import { Typography, useScreenSize } from '@banhala/ui-kit';
import { useEffect, useState } from 'react';
import { StyleSheet, TextInput, View } from 'react-native';
import { Flex, ColorValues } from '@banhala/ui-kit';
import { isWeb } from '../../../../../../utils';
import type { SetFilterListBottomSheetViaQueryValueProps } from '../../Provider/filterListBottomSheet/useFilterListBottomSheetAtom';
import { useFilterHeader } from '../useFilterHeader';
import type { SetState } from '../../../../../../types';
import type { SetListHeight } from '../../hooks/useListHeight';
import { RenderItemWrapper } from '../RenderItemWrapper';

export interface ISlideChildGroup {
  title: string;
  type: FilterType.Slide;
  entity: {
    unitText: string;
    minQueryName: string;
    maxQueryName: string;
    rawMaxValue: number | null;
    rawMinValue: number | null;
    selectedMin: number;
    selectedMax: number;
    interval: number;
    min: number;
    max: number;
    countList: number[];
  };
  handleClickFilter: (
    props: SetFilterListBottomSheetViaQueryValueProps & { title: string },
  ) => void;
  isResetSlider: boolean;
  setIsResetSlider: SetState<boolean>;
  setListHeight: SetListHeight;
  index: number;
}

const TOTAL_PADDING = 32;
const SLIDE_DOT_SIZE = 30;
export const SlideFilter = ({
  title,
  entity,
  handleClickFilter,
  isResetSlider,
  setIsResetSlider,
  setListHeight,
  index,
}: ISlideChildGroup) => {
  const { Header } = useFilterHeader({ title });
  const min = entity.min * entity.interval;
  const max = entity.max * entity.interval;
  const selectedMin = entity.selectedMin * entity.interval;
  const selectedMax = entity.selectedMax * entity.interval;
  const [multiSliderValue, setMultiSliderValue] = useState([
    selectedMin,
    selectedMax,
  ]);

  const [inputMinValue, setInputMinValue] = useState(selectedMin.toString());
  const [inputMaxValue, setInputMaxValue] = useState(selectedMax.toString());

  const { width } = useScreenSize();

  useEffect(() => {
    setInputMinValue(selectedMin.toString());
    setInputMaxValue(selectedMax.toString());
  }, [selectedMin, selectedMax]);

  useEffect(() => {
    if (isResetSlider) {
      setMultiSliderValue([min, max]);
      setInputMinValue(min.toString());
      setInputMaxValue(max.toString());
    }
    setIsResetSlider(false);
  }, [isResetSlider, min, max, setIsResetSlider]);

  const handleMinChange = (text: string) => {
    const numericValue = text.replace(/[^0-9]/g, '');
    setInputMinValue(numericValue);
  };

  const handleMaxChange = (text: string) => {
    const numericValue = text.replace(/[^0-9]/g, '');
    setInputMaxValue(numericValue);
  };

  const handleSubmit = () => {
    const minInputValue = inputMinValue === '' ? 0 : Number(inputMinValue);
    const maxInputValue = inputMaxValue === '' ? 0 : Number(inputMaxValue);

    let clampedMin = (() => {
      if (minInputValue < min || minInputValue > max) {
        return min;
      }
      return minInputValue;
    })();
    let clampedMax = (() => {
      if (maxInputValue < min || maxInputValue > max) {
        return max;
      }
      return maxInputValue;
    })();

    if (clampedMin > clampedMax) {
      [clampedMin, clampedMax] = [clampedMax, clampedMin];
    }

    setMultiSliderValue([clampedMin, clampedMax]);
    setInputMinValue(clampedMin.toString());
    setInputMaxValue(clampedMax.toString());
    handleClickFilter({
      queryName: entity.minQueryName,
      queryValue: [clampedMin.toString()],
      title,
    });
    handleClickFilter({
      queryName: entity.maxQueryName,
      queryValue: [clampedMax.toString()],
      title,
    });
  };
  return (
    <RenderItemWrapper
      setListHeight={setListHeight}
      index={index}
      title={title}
    >
      <Header />
      <Flex.Row gap={12} alignItems="center">
        <Flex flex={1} flexShrink={1}>
          <TextInput
            style={styles.TextInput}
            value={
              inputMinValue === '' ? '' : Number(inputMinValue).toLocaleString()
            }
            onChangeText={handleMinChange}
            keyboardType="number-pad"
            placeholder={min.toLocaleString()}
            placeholderTextColor={ColorValues.gray50}
            returnKeyType="done"
            onSubmitEditing={handleSubmit}
          />
        </Flex>
        <Typography type="subtitle1">~</Typography>
        <Flex flex={1} flexShrink={1}>
          <TextInput
            style={styles.TextInput}
            value={
              inputMaxValue === '' ? '' : Number(inputMaxValue).toLocaleString()
            }
            onChangeText={handleMaxChange}
            keyboardType="number-pad"
            placeholder={max.toLocaleString()}
            placeholderTextColor={ColorValues.gray50}
            returnKeyType="done"
            onSubmitEditing={handleSubmit}
          />
        </Flex>
        {isWeb && (
          <Flex
            onPress={handleSubmit}
            height={46}
            width={49.5}
            alignItems="center"
            justifyContent="center"
          >
            <Typography type="subtitle2" color="content_blue_primary">
              적용
            </Typography>
          </Flex>
        )}
      </Flex.Row>
      {min !== max ? (
        <MultiSlider
          sliderLength={width - TOTAL_PADDING - SLIDE_DOT_SIZE}
          trackStyle={{ height: 6, backgroundColor: 'rgb(37, 114, 247)' }}
          containerStyle={{ margin: 'auto' }}
          step={entity.interval}
          min={min}
          max={max}
          allowOverlap
          customMarker={CustomMarker}
          selectedStyle={{
            backgroundColor: 'rgb(37, 114, 247)',
          }}
          unselectedStyle={{
            backgroundColor: 'rgb(221, 221, 221)',
          }}
          onValuesChangeFinish={handleSubmit}
          values={[multiSliderValue[0], multiSliderValue[1]]}
          onValuesChange={v => {
            if (v.length === 2) {
              setMultiSliderValue(v);
              setInputMinValue(v[0].toString());
              setInputMaxValue(v[1].toString());
            }
          }}
        />
      ) : (
        <MultiSlider
          sliderLength={width - TOTAL_PADDING - SLIDE_DOT_SIZE}
          trackStyle={{ height: 6 }}
          containerStyle={{ margin: 'auto' }}
          customMarker={CustomMarker}
          values={[0]}
          onValuesChange={() => {}}
          snapped={false}
          enabledOne={false}
        />
      )}
    </RenderItemWrapper>
  );
};
const CustomMarker = () => {
  return (
    <View
      style={{
        boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 6px',
        width: 30,
        height: 30,
        backgroundColor: 'white',
        borderRadius: 9999,
      }}
    />
  );
};
const styles = StyleSheet.create({
  TextInput: {
    width: '100%',
    height: 48,
    fontSize: 16,
    borderRadius: 3,
    borderWidth: 1,
    borderColor: ColorValues.gray30,
    textAlign: 'right',
    paddingHorizontal: 12,
    paddingTop: 14,
    paddingBottom: 14,
    includeFontPadding: false,
    textAlignVertical: 'center',
  },
});
